import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import { DEFAULT_PAGE_LAYOUT_GAP, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { GoogleMap } from '@interxfi/app-components/components/map/google-map';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  type WrappingLayoutColumnDef,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { BaseLink } from '@interxfi/design-system/components/link/base-link';
import { TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Typography } from '@interxfi/design-system/components/typography';
import HistoryIcon from '@interxfi/design-system/icons/react/icon-history';
import { IxcRegistrationV1ControlsMarginType } from '@interxfi/types/generated/api';
import { AccountBreadcrumb } from '../../../../../../../features/registration/account/account-breadcrumb';
import { AccountMarginTypePill } from '../../../../../../../features/registration/account/account-margin-type-pill';
import { AccountRegistrationOrPurposePill } from '../../../../../../../features/registration/account/account-registration-or-purpose-pill';
import { useAccountPrimaryAddress } from '../../../../../../../features/registration/account/account-registration-utils';
import { AccountStatusPill } from '../../../../../../../features/registration/account/account-status-pill';
import { RegistrationAccountEventDrawer } from '../../../../../../../features/registration/account/registration-account-event';
import { RegistrationAccountPageHeader } from '../../../../../../../features/registration/account/registration-account-page-header';
import { formatAddress, ONE_LINE_ADDRESS_FORMAT_LAYOUTS } from '../../../../../../../features/registration/shared-utils/address';
import { getPageHead } from '../../../../../../../util/routing/meta';
import styles from './_overview.module.scss';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_overview')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-asset`,
        label: <AccountBreadcrumb accountId={params.accountId} displayIdOnly />,
        to: `/registration/account/${params.accountId}/asset`,
        suffix: <CopyButton content={params.accountId} label="Copy Account ID" tooltip={{ copy: params.accountId, copied: 'Copied Account ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Account'),
  component: AccountOverviewLayout,
});

const rightColumnContainer = {
  width: { type: 'fixed', width: 320 },
} as const satisfies Omit<WrappingLayoutColumnDef, 'render'>;

const leftColumnContainer = { width: { type: 'min', min: 592 } } satisfies Omit<WrappingLayoutColumnDef, 'render'>;

function AccountOverviewLayout() {
  const { accountId } = Route.useParams();
  const request = { accountId } as const;
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet(request);
  const showPending = Boolean(isPending || error);
  const primaryAddress = useAccountPrimaryAddress(accountId);
  const addressLabel = !data?.account?.data?.spec || 'customer' in data.account.data.spec ? 'Primary Profile Physical Address' : 'Physical Address';
  const formattedPrimaryAddress = primaryAddress ? formatAddress(primaryAddress, ONE_LINE_ADDRESS_FORMAT_LAYOUTS) : undefined;
  const mapsUrl = formattedPrimaryAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedPrimaryAddress)}`
    : undefined;

  return (
    <PageLayout gap={7} maxWidthContainer="976px">
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          actions={
            <RegistrationAccountEventDrawer
              request={request}
              trigger={<Button affixIcon={<HistoryIcon aria-hidden width={16} />} size="small" variant="light" />}
            />
          }
          pills={
            <>
              <AccountStatusPill isPending={showPending} size="large" status={data?.account?.status} />
              <AccountRegistrationOrPurposePill account={data?.account} isPending={showPending} size="large" />
              <AccountMarginTypePill
                isPending={showPending}
                marginType={data?.account?.data?.controls?.registration?.settings?.marginType || IxcRegistrationV1ControlsMarginType.Cash}
                size="large"
              />
            </>
          }
        />
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...leftColumnContainer,
            render: <Outlet />,
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...rightColumnContainer,
            render: (
              <VerticalStack gap={7}>
                {mapsUrl ? (
                  <BaseLink aria-label={`Open ${formattedPrimaryAddress} in Google Maps`} className={styles.mapLink} target="_blank" to={mapsUrl}>
                    <GoogleMap address={formattedPrimaryAddress} apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} />
                  </BaseLink>
                ) : (
                  <GoogleMap address={formattedPrimaryAddress} apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} />
                )}

                {formattedPrimaryAddress && mapsUrl ? (
                  <VerticalStack gap={2} inlineAlign="center">
                    <Typography align="center" lineHeight="1.125rem" size="0.875rem">
                      {addressLabel}
                    </Typography>
                    <TextLink
                      size="small"
                      target="_blank"
                      to={mapsUrl}
                      typography={{ align: 'center', lineHeight: '1.125rem', size: '0.875rem', tone: 'neutral-dk' }}
                    >
                      {formattedPrimaryAddress}
                    </TextLink>
                  </VerticalStack>
                ) : null}
              </VerticalStack>
            ),
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
